/**
 * 加载单个JS资源，并插入 `<head>`
 *
 * ```js
 * FastUtils.loadScript('...').then(function(){
 *   console.log('load success');
 * }).catch(function(err){
 *   console.error('load fail', err);
 * });
 * ```
 *
 * @category Loader
 * @param src
 * @param opts.crossOrigin 添加 crossOrigin 属性
 */
function loadScript(src, opts) {
    if (opts === void 0) { opts = { crossOrigin: '' }; }
    return new Promise(function (resolve, reject) {
        var head = document.getElementsByTagName('head')[0];
        var script = document.createElement('script');
        script.type = 'text/javascript';
        if (opts.crossOrigin === 'anonymous' || opts.crossOrigin === 'use-credentials') {
            script.crossOrigin = opts.crossOrigin;
        }
        script.src = src;
        script.onload = function () {
            resolve();
        };
        script.onerror = function () {
            reject(new Error(src + " \u52A0\u8F7D\u5931\u8D25"));
        };
        head.appendChild(script);
    });
}
/**
 * 加载多个JS资源，并插入 `<head>`
 *
 * ```js
 * // 默认串行加载，加载完一个再加载下一个
 * FastUtils.loadScriptList({ srcList: ['...', '...'] }).then(function(){
 *   console.log('load success');
 * }).catch(function(err){
 *   console.error('load fail', err);
 * });
 *
 * // 并行加载，同时加载所有
 * FastUtils.loadScriptList({ srcList: ['...', '...'], parallel: true }).then(function(){
 *   console.log('load success');
 * }).catch(function(err){
 *   console.error('load fail', err);
 * });
 * ```
 *
 * @category Loader
 * @param params.srcList 资源数组
 * @param params.parallel 是否并行，默认否
 * @param params.srcOpts 加载参数, 默认无
 */
function loadScriptList(params) {
    if (params.srcList.length === 0)
        return Promise.reject();
    var srcOpts = params.srcOpts || {};
    if (params.srcList.length === 1) {
        return loadScript(params.srcList[0], srcOpts);
    }
    else if (params.parallel === true) {
        // 并行
        var loadPromises = params.srcList.map(function (src) { return loadScript(src, srcOpts); });
        return Promise.all(loadPromises);
    }
    else {
        // 串行
        return params.srcList.reduce(function (previousPromise, currentSrc) {
            return previousPromise.then(function () {
                return loadScript(currentSrc, srcOpts);
            });
        }, Promise.resolve());
    }
}
/**
 * 加载单个CSS资源，并插入 `<head>`
 *
 * ```js
 * FastUtils.loadStyle('...').then(function(){
 *   console.log('load success');
 * }).catch(function(err){
 *   console.error('load fail', err);
 * });
 * ```
 *
 * @category Loader
 * @param src
 */
function loadStyle(src) {
    return new Promise(function (resolve, reject) {
        var head = document.getElementsByTagName('head')[0];
        var style = document.createElement('link');
        style.rel = 'stylesheet';
        style.type = 'text/css';
        style.href = src;
        style.onload = function () {
            resolve();
        };
        style.onerror = function () {
            reject(new Error(src + " \u52A0\u8F7D\u5931\u8D25"));
        };
        head.appendChild(style);
    });
}
/**
 * 加载多个CSS资源，并插入 `<head>`
 *
 * ```js
 * // 并行加载，同时加载所有
 * FastUtils.loadStyleList({ srcList: ['...', '...'] }).then(function(){
 *   console.log('load success');
 * }).catch(function(err){
 *   console.error('load fail', err);
 * });
 * ```
 *
 * @category Loader
 * @param params.srcList 资源数组
 */
function loadStyleList(params) {
    if (params.srcList.length === 0)
        return Promise.reject();
    if (params.srcList.length === 1) {
        return loadStyle(params.srcList[0]);
    }
    else {
        // 并行
        var loadPromises = params.srcList.map(function (src) { return loadStyle(src); });
        return Promise.all(loadPromises);
    }
}
/**
 * 加载单个Image资源
 *
 * ```js
 * FastUtils.loadImage('...').then(function(img){
 *   console.log('load success');
 *   document.body.appendChild(img);
 * }).catch(function(err){
 *   console.error('load fail', err);
 * });
 * ```
 *
 * @category Loader
 * @param src
 * @return 返回 `HTMLImageElement`，可以插入 HTML
 */
function loadImage(src) {
    return new Promise(function (resolve, reject) {
        var img = new Image();
        img.src = src;
        img.onload = function () {
            resolve(img);
        };
        img.onerror = function () {
            reject(new Error(src + " \u52A0\u8F7D\u5931\u8D25"));
        };
    });
}
/**
 * 加载多个Image资源
 *
 * ```js
 * // 并行加载，同时加载所有
 * FastUtils.loadImageList({
 *   srcList: ['...', '...'],
 *   onProgress: function(params) {
 *     console.log('current progress:', params.percent);
 *   }
 * }).then(function(imgList){
 *   console.log('load success', imgList);
 * }).catch(function(err){
 *   console.error('load fail', err);
 * });
 * ```
 *
 * @category Loader
 * @param params.srcList 图片资源列表
 * @param params.onProgress 图片加载进度通知，可实现加载进度条
 * @return 返回 `HTMLImageElement` 列表，可以插入 HTML
 */
function loadImageList(params) {
    if (params.srcList.length === 0)
        return Promise.reject();
    // 并行
    var total = params.srcList.length;
    var completed = 0;
    var loadPromises = [];
    params.srcList.forEach(function (src) {
        var loadItem = loadImage(src);
        loadPromises.push(loadItem);
        loadItem.then(function () {
            completed++;
            if (typeof params.onProgress === 'function') {
                params.onProgress({ total: total, completed: completed, percent: completed / total });
            }
        });
    });
    return Promise.all(loadPromises);
}
export { loadScript, loadScriptList, loadStyle, loadStyleList, loadImage, loadImageList };



//////////////////
// WEBPACK FOOTER
// ./node_modules/@ali/fast-utils/dist/loader.js
// module id = null
// module chunks = 